import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

import { DashboardRecord, isDashboardRecord } from "@/shared/domain/dashboards";
import { useUpdateDashboard } from "@/shared/domain/dashboards/hooks";
import { Button } from "@/shared/foundation/primitives";
import {
  RobotoDashboardRevisionConflictException,
  userFacingErrorMessage,
} from "@/shared/http";

import { DashboardDefinition } from "../DashboardHeader/DashboardParams";

import styles from "./DashboardSwitchGuard.module.css";

export interface DashboardSwitchGuardProps {
  /** The dashboard being switched AWAY from: the loaded record, or null when
   *  the edits belong to an unsaved view. A null record has nothing to update,
   *  which is what the "Save as…" primary action below is for. */
  dashboard: DashboardRecord | null;
  /** Snapshot of current view state, persisted on Save. */
  currentDefinition: DashboardDefinition;
  /** The revision the canvas was seeded from, sent with the save so a stale
   *  write is rejected rather than clobbering someone else's edit. */
  baseRevision: number | null;
  /** Abandon the switch and stay put with the edits intact. */
  onKeepEditing: () => void;
  /** Throw the edits away — the owner clears its localStorage draft. Paired
   *  with `onProceed`, which the owner calls to complete the switch. */
  onDiscard: () => void;
  /** A Save succeeded; the passed record now matches the live view. */
  onSaved: (saved: DashboardRecord) => void;
  /** The Save was rejected because someone else changed the dashboard first.
   *  The switch stays held: the user's work is still unpersisted, and the
   *  conflict dialog owns the resolution. */
  onRevisionConflict: (serverRecord: DashboardRecord) => void;
  /** Complete the held switch. Called after a successful save or a discard. */
  onProceed: () => void;
  /** Unsaved view only: open the Save-as dialog, since there is no record to
   *  update. Deliberately does NOT chain the held switch — naming a dashboard
   *  and then being moved off it reads as the app losing your place. */
  onSaveAs: () => void;
}

/**
 * Asks what to do with unsaved layout edits when the user picks a DIFFERENT
 * dashboard — the one moment a working copy is about to be replaced.
 *
 * Deliberately not a leave guard. The draft persists and auto-loads, so
 * closing the tab or wandering off to another page loses nothing and is never
 * interrupted; only swapping the canvas out from under the edits is worth a
 * question. (This component used to block every in-app navigation through
 * react-router's `useBlocker` plus a `beforeunload` handler, which — because
 * the dashboard id is a path segment — fired on every dashboard switch AND
 * every trip elsewhere in the app.)
 *
 * Mounted only while a switch is held, so it has no idle state of its own:
 * dismissing it unmounts it, taking the save mutation's error with it.
 */
export function DashboardSwitchGuard({
  dashboard,
  currentDefinition,
  baseRevision,
  onKeepEditing,
  onDiscard,
  onSaved,
  onRevisionConflict,
  onProceed,
  onSaveAs,
}: DashboardSwitchGuardProps) {
  const updateMutation = useUpdateDashboard();
  const pending = updateMutation.isPending;

  // Dismissing (Escape / backdrop / "Keep editing") abandons the switch. That
  // must never happen while a save is in flight: the pending save's outcome
  // still needs the held switch to proceed into, or the error to render over.
  const handleClose = () => {
    if (pending) {
      return;
    }
    onKeepEditing();
  };

  const handleSave = () => {
    if (dashboard === null || baseRevision === null) {
      // An unsaved view routes to Save-as instead; this is belt-and-braces.
      // A definition write must never go out without a base revision.
      return;
    }
    updateMutation.mutate(
      {
        dashboardId: dashboard.dashboard_id,
        updates: {
          dashboard_definition: currentDefinition,
          base_revision: baseRevision,
        },
      },
      {
        onSuccess: (record) => {
          onSaved(record);
          onProceed();
        },
        onError: (error) => {
          // Deliberately does NOT proceed: the save failed, so the user's work
          // is still unpersisted and switching would strand it. A conflict
          // hands off to the conflict dialog, whose doors resolve the switch.
          if (error instanceof RobotoDashboardRevisionConflictException) {
            const serverRecord = error.currentDashboard;
            if (isDashboardRecord(serverRecord)) {
              onRevisionConflict(serverRecord);
            }
          }
        },
      },
    );
  };

  const handleDiscard = () => {
    onDiscard();
    onProceed();
  };

  const isUnsavedView = dashboard === null;

  return (
    <Dialog open onClose={handleClose}>
      <DialogTitle>Unsaved dashboard changes</DialogTitle>
      <DialogContent>
        <div className={styles.dialogBody}>
          Your changes to{" "}
          <strong>{dashboard?.name ?? "this new dashboard"}</strong>{" "}
          haven&apos;t been saved. Save them before loading another dashboard?
          {/* MUI Alert carries role="alert", so the failure is announced to
           * screen readers when it appears mid-dialog — the same pattern as
           * the dashboard's load-error surface (MetricsDashboard.tsx) and
           * the visualizer's SaveLayoutConfirmation. */}
          {updateMutation.error &&
            !(
              updateMutation.error instanceof
              RobotoDashboardRevisionConflictException
            ) && (
              <Alert severity="error" className={styles.error}>
                {/* A conflict owns its own dialog, so it is excluded here
                 * rather than duplicated alongside it. */}
                {userFacingErrorMessage(
                  updateMutation.error,
                  "Failed to save dashboard. Please try again.",
                )}
              </Alert>
            )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={pending}>
          Keep editing
        </Button>
        <Button variant="outlined" onClick={handleDiscard} disabled={pending}>
          Discard changes
        </Button>
        {isUnsavedView ? (
          <Button variant="contained" onClick={onSaveAs}>
            Save as…
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSave} disabled={pending}>
            {pending ? "Saving…" : "Save changes"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
